/**
 * Centralized mock data configuration
 * This ensures consistency across all mock data generation throughout the application
 * ALL mock price generation should use these values to maintain data integrity
 */

export interface MockPriceConfig {
  basePrice: number;
  dailyVolatility: number; // as decimal (0.02 = 2%)
  trend: 'bullish' | 'bearish' | 'sideways';
}

/**
 * Production-ready mock prices based on actual market data (as of late 2024)
 * These prices are used when live market data is unavailable
 * WARNING: These are MOCK prices and should be clearly indicated to users
 */
export const MOCK_STOCK_PRICES: Record<string, MockPriceConfig> = {
  // Large Cap Tech
  'AAPL': { basePrice: 203.92, dailyVolatility: 0.015, trend: 'sideways' },
  'MSFT': { basePrice: 442.11, dailyVolatility: 0.012, trend: 'bullish' },
  'GOOGL': { basePrice: 172.18, dailyVolatility: 0.018, trend: 'sideways' },
  'GOOG': { basePrice: 173.05, dailyVolatility: 0.018, trend: 'sideways' },
  'AMZN': { basePrice: 173.95, dailyVolatility: 0.020, trend: 'sideways' },
  'META': { basePrice: 551.34, dailyVolatility: 0.025, trend: 'bullish' },
  'TSLA': { basePrice: 248.42, dailyVolatility: 0.035, trend: 'bearish' },
  'NVDA': { basePrice: 131.52, dailyVolatility: 0.030, trend: 'sideways' },
  'AMD': { basePrice: 144.96, dailyVolatility: 0.025, trend: 'sideways' },
  'NFLX': { basePrice: 706.19, dailyVolatility: 0.022, trend: 'sideways' },
  'INTC': { basePrice: 23.45, dailyVolatility: 0.020, trend: 'bearish' },
  'ORCL': { basePrice: 172.34, dailyVolatility: 0.015, trend: 'bullish' },

  // ETFs
  'SPY': { basePrice: 578.45, dailyVolatility: 0.008, trend: 'bullish' },
  'QQQ': { basePrice: 505.23, dailyVolatility: 0.012, trend: 'bullish' },
  'IWM': { basePrice: 224.67, dailyVolatility: 0.015, trend: 'sideways' },
  'VTI': { basePrice: 298.34, dailyVolatility: 0.008, trend: 'bullish' },
  'DIA': { basePrice: 434.56, dailyVolatility: 0.008, trend: 'bullish' },

  // Consumer Goods
  'KO': { basePrice: 62.78, dailyVolatility: 0.010, trend: 'sideways' },
  'PEP': { basePrice: 162.45, dailyVolatility: 0.010, trend: 'sideways' },
  'WMT': { basePrice: 91.23, dailyVolatility: 0.012, trend: 'bullish' },
  'MCD': { basePrice: 293.45, dailyVolatility: 0.010, trend: 'sideways' },

  // Financial
  'JPM': { basePrice: 243.67, dailyVolatility: 0.018, trend: 'bullish' },
  'BAC': { basePrice: 45.23, dailyVolatility: 0.020, trend: 'sideways' },
  'GS': { basePrice: 567.89, dailyVolatility: 0.025, trend: 'bullish' },

  // Healthcare
  'JNJ': { basePrice: 152.34, dailyVolatility: 0.012, trend: 'sideways' },
  'PFE': { basePrice: 25.67, dailyVolatility: 0.015, trend: 'bearish' },
  'UNH': { basePrice: 598.45, dailyVolatility: 0.015, trend: 'bullish' },
};

/**
 * Default configuration for symbols not in the main list
 */
export const DEFAULT_MOCK_CONFIG: MockPriceConfig = {
  basePrice: 100,
  dailyVolatility: 0.020,
  trend: 'sideways'
};

/**
 * Get mock price configuration for a symbol
 * @param symbol Stock symbol (case insensitive)
 * @returns Mock price configuration
 */
export function getMockPriceConfig(symbol: string): MockPriceConfig {
  const symbolUpper = symbol.toUpperCase().trim();
  return MOCK_STOCK_PRICES[symbolUpper] || {
    ...DEFAULT_MOCK_CONFIG,
    basePrice: DEFAULT_MOCK_CONFIG.basePrice + (symbolUpper.charCodeAt(0) - 65) * 5 // Small variation based on symbol
  };
}

/**
 * Generate a mock price with realistic variation
 * @param symbol Stock symbol
 * @param daysFromBase Number of days from the base price (for historical simulation)
 * @returns Generated price
 */
export function generateMockPrice(symbol: string, daysFromBase: number = 0): number {
  const config = getMockPriceConfig(symbol);
  
  // Apply trend over time
  const trendMultiplier = getTrendMultiplier(config.trend, daysFromBase);
  
  // Apply daily volatility
  const randomFactor = 1 + (Math.random() - 0.5) * config.dailyVolatility * 2;
  
  return config.basePrice * trendMultiplier * randomFactor;
}

/**
 * Get trend multiplier based on trend type and time
 * @param trend Trend direction
 * @param days Number of days
 * @returns Multiplier to apply to base price
 */
function getTrendMultiplier(trend: 'bullish' | 'bearish' | 'sideways', days: number): number {
  const maxTrendEffect = 0.20; // Maximum 20% trend effect over 365 days
  const trendFactor = Math.min(Math.abs(days) / 365, 1) * maxTrendEffect;
  
  switch (trend) {
    case 'bullish':
      return 1 + (days >= 0 ? trendFactor : -trendFactor);
    case 'bearish':
      return 1 - (days >= 0 ? trendFactor : -trendFactor);
    case 'sideways':
    default:
      return 1 + Math.sin(days / 30) * 0.05; // Small cyclical variation
  }
}

/**
 * Validation function to ensure mock data is clearly marked
 * This should be called whenever mock data is returned to users
 */
export function validateMockDataUsage(context: string): void {
  if (process.env.NODE_ENV === 'production') {
    console.warn(`⚠️  MOCK DATA WARNING: ${context} - Using simulated market data. This should be clearly indicated to users.`);
  }
} 